const COMPRESS_IF_LARGER_THAN = 40 * 1024 * 1024;
const TARGET_MAX_WIDTH = 1280;
const TARGET_FPS = 24;
const VIDEO_BITS_PER_SECOND = 1800000;
const AUDIO_BITS_PER_SECOND = 96000;

const compressJobs = new WeakMap();

function pickRecorderMimeType() {
    const types = [
        "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
        "video/mp4",
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
    ];
    if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
        return "";
    }
    return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

function evenSize(value) {
    const rounded = Math.max(2, Math.round(Number(value) || 0));
    return rounded % 2 === 0 ? rounded : rounded + 1;
}

function waitForEvent(target, eventName) {
    return new Promise((resolve, reject) => {
        const onSuccess = () => {
            cleanup();
            resolve();
        };
        const onError = () => {
            cleanup();
            reject(new Error("Unable to read the video file"));
        };
        const cleanup = () => {
            target.removeEventListener(eventName, onSuccess);
            target.removeEventListener("error", onError);
        };
        target.addEventListener(eventName, onSuccess, { once: true });
        target.addEventListener("error", onError, { once: true });
    });
}

function createRecorder(stream, mimeType) {
    const options = {
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
    };
    if (mimeType) {
        options.mimeType = mimeType;
    }
    try {
        return new MediaRecorder(stream, options);
    } catch (error) {
        try {
            return new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        } catch (fallbackError) {
            return new MediaRecorder(stream);
        }
    }
}

async function recaptureAtLowerBitrate(file, onProgress) {
    const mimeType = pickRecorderMimeType();
    if (!mimeType && typeof MediaRecorder === "undefined") {
        return file;
    }

    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.playsInline = true;
    video.setAttribute("playsinline", "true");
    video.preload = "auto";
    video.controls = false;
    video.muted = false;
    video.volume = 0;
    video.src = objectUrl;
    video.style.cssText = "position:fixed;left:-9999px;top:0;width:2px;height:2px;opacity:0;pointer-events:none;";
    document.body.appendChild(video);

    let recorder;
    let audioContext;
    let drawId = 0;
    let canvas;
    const chunks = [];

    const cleanup = () => {
        if (drawId) {
            cancelAnimationFrame(drawId);
            drawId = 0;
        }
        try {
            if (recorder && recorder.state !== "inactive") {
                recorder.stop();
            }
        } catch (error) {
            /* ignore */
        }
        try {
            video.pause();
        } catch (error) {
            /* ignore */
        }
        if (audioContext) {
            audioContext.close().catch(() => {});
            audioContext = null;
        }
        try {
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            /* ignore */
        }
        video.removeAttribute("src");
        try {
            video.load();
        } catch (error) {
            /* ignore */
        }
        if (video.parentNode) {
            video.parentNode.removeChild(video);
        }
        if (canvas && canvas.parentNode) {
            canvas.parentNode.removeChild(canvas);
        }
    };

    try {
        await Promise.race([
            waitForEvent(video, "loadedmetadata"),
            new Promise((_, reject) => window.setTimeout(() => reject(new Error("metadata timeout")), 12000)),
        ]);

        const sourceWidth = video.videoWidth || TARGET_MAX_WIDTH;
        const sourceHeight = video.videoHeight || 720;
        const scale = Math.min(1, TARGET_MAX_WIDTH / sourceWidth);
        const outW = evenSize(sourceWidth * scale);
        const outH = evenSize(sourceHeight * scale);

        canvas = document.createElement("canvas");
        canvas.width = outW;
        canvas.height = outH;
        canvas.style.cssText = "position:fixed;left:-9999px;top:0;width:2px;height:2px;opacity:0;pointer-events:none;";
        document.body.appendChild(canvas);
        const ctx = canvas.getContext("2d", { alpha: false });
        if (!ctx) {
            cleanup();
            return file;
        }

        const canvasStream = canvas.captureStream(TARGET_FPS);
        const capture =
            typeof video.captureStream === "function"
                ? video.captureStream()
                : typeof video.mozCaptureStream === "function"
                    ? video.mozCaptureStream()
                    : null;

        if (capture) {
            capture.getAudioTracks().forEach((track) => canvasStream.addTrack(track));
        } else if (typeof AudioContext !== "undefined") {
            audioContext = new AudioContext();
            const source = audioContext.createMediaElementSource(video);
            const dest = audioContext.createMediaStreamDestination();
            source.connect(dest);
            dest.stream.getAudioTracks().forEach((track) => canvasStream.addTrack(track));
        }

        recorder = createRecorder(canvasStream, mimeType);
        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size) {
                chunks.push(event.data);
            }
        };

        const recorded = new Promise((resolve, reject) => {
            recorder.onerror = () => reject(new Error("Compression failed"));
            recorder.onstop = () => {
                const type = recorder.mimeType || mimeType || "video/webm";
                resolve(new Blob(chunks, { type }));
            };
        });

        video.currentTime = 0;
        try {
            await video.play();
        } catch (playError) {
            video.muted = true;
            await video.play();
        }
        recorder.start(1000);

        const draw = () => {
            if (video.ended || recorder.state !== "recording") {
                return;
            }
            ctx.drawImage(video, 0, 0, outW, outH);
            if (typeof onProgress === "function" && video.duration) {
                onProgress(Math.min(0.99, video.currentTime / video.duration));
            }
            drawId = requestAnimationFrame(draw);
        };
        draw();

        const ended = waitForEvent(video, "ended");
        const timeoutMs = Math.max(20000, (Number(video.duration) || 60) * 1000 + 20000);
        await Promise.race([
            ended,
            new Promise((_, reject) => window.setTimeout(() => reject(new Error("compression timeout")), timeoutMs)),
        ]).catch(() => {
            try {
                video.pause();
            } catch (error) {
                /* ignore */
            }
        });
        if (typeof onProgress === "function") {
            onProgress(1);
        }
        if (recorder.state === "recording") {
            recorder.stop();
        }

        const blob = await recorded;
        cleanup();

        if (!blob || blob.size < 1) {
            return file;
        }

        const extension = (blob.type || "").includes("mp4") ? "mp4" : "webm";
        const compressedFile = new File(
            [blob],
            String(file.name || "video").replace(/\.[^.]+$/, "") + "-compressed." + extension,
            { type: blob.type || (extension === "mp4" ? "video/mp4" : "video/webm") }
        );
        return compressedFile;
    } catch (error) {
        cleanup();
        throw error;
    }
}

export async function compressVideoForUpload(file, onProgress) {
    if (!file || file.size < COMPRESS_IF_LARGER_THAN) {
        return file;
    }
    try {
        const compressed = await recaptureAtLowerBitrate(file, onProgress);
        if (compressed && compressed.size > 0 && compressed.size < file.size * 0.9) {
            return compressed;
        }
    } catch (error) {
        console.warn("Video compression skipped", error);
    }
    return file;
}

export function startVideoCompression(file, onProgress) {
    if (!file) {
        return Promise.resolve(file);
    }
    let job = compressJobs.get(file);
    if (!job) {
        job = {
            listeners: [],
            promise: compressVideoForUpload(file, (ratio) => {
                job.listeners.forEach((listener) => listener(ratio));
            }),
        };
        compressJobs.set(file, job);
    }
    if (typeof onProgress === "function") {
        job.listeners.push(onProgress);
    }
    return job.promise;
}
